import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowDown, Check, Flag, MessageSquareText, Pencil, Send, Trash2, X,
} from 'lucide-react';
import { useAuth } from '../../context';
import useThread from '../../hooks/useThread';
import { apiGetMentionCandidates } from '../../api';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { PersonAvatar, UserIdentity } from '../People/PeopleUI';

const fmtTime = (value) => new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const isSameDay = (first, second) => {
  const a = new Date(first);
  const b = new Date(second);
  return a.toDateString() === b.toDateString();
};

const fmtDay = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const authorIdOf = (message) => String(message?.author?._id || message?.author || '');

const renderMessageBody = (body = '') => body
  .split(/(@[a-z0-9][a-z0-9._-]{0,31})/gi)
  .map((part, index) => (
    /^@[a-z0-9][a-z0-9._-]{0,31}$/i.test(part)
      ? <span className="thread-mention" key={`${part}-${index}`}>{part}</span>
      : part
  ));

const mentionAtCaret = (value, caret) => {
  const beforeCaret = value.slice(0, caret);
  const match = beforeCaret.match(/(^|\s)@([a-z0-9._-]*)$/i);
  if (!match) return null;
  const start = beforeCaret.lastIndexOf('@');
  return { start, end: caret, query: match[2].toLowerCase() };
};

export default function Thread({
  threadType,
  threadId,
  height = 'min(60vh, 520px)',
  placeholder = 'Write a message…',
  enabled = true,
  pendingMilestone = null,
  onPendingMilestoneChange,
  onMilestoneClick,
  onMessageSent,
  onMessageDeleted,
}) {
  const { jwtToken, user } = useAuth();
  const { messages, loading, error, sending, send, edit, remove } = useThread(jwtToken, threadType, threadId, { enabled });
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [activeMention, setActiveMention] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [atBottom, setAtBottom] = useState(true);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const stickToBottom = useRef(true);
  const directMessageHandled = useRef('');
  const requestedMessage = new URLSearchParams(window.location.search).get('message');

  useEffect(() => {
    let cancelled = false;
    setMentionCandidates([]);
    setActiveMention(null);
    if (!enabled || !jwtToken || !threadId) return undefined;
    apiGetMentionCandidates(jwtToken, threadType, threadId)
      .then((people) => { if (!cancelled) setMentionCandidates(Array.isArray(people) ? people : []); })
      .catch(() => { if (!cancelled) setMentionCandidates([]); });
    return () => { cancelled = true; };
  }, [enabled, jwtToken, threadType, threadId]);

  useLayoutEffect(() => {
    const field = textareaRef.current;
    if (!field) return;
    field.style.height = '0px';
    const nextHeight = Math.min(Math.max(field.scrollHeight, 48), 180);
    field.style.height = `${nextHeight}px`;
    field.style.overflowY = field.scrollHeight > 180 ? 'auto' : 'hidden';
  }, [draft]);

  const mentionMatches = activeMention
    ? mentionCandidates.filter((person) => {
      const query = activeMention.query;
      return !query
        || person.handle.toLowerCase().includes(query)
        || person.name.toLowerCase().includes(query);
    })
    : [];

  const onScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    stickToBottom.current = nearBottom;
    setAtBottom(nearBottom);
  };

  const jumpToLatest = () => {
    stickToBottom.current = true;
    setAtBottom(true);
    const container = scrollRef.current;
    if (!container) return;
    if (typeof container.scrollTo === 'function') container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    else container.scrollTop = container.scrollHeight;
  };

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (stickToBottom.current && container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!requestedMessage || directMessageHandled.current === requestedMessage || loading) return;
    const container = scrollRef.current;
    const row = document.getElementById(`thread-message-${requestedMessage}`);
    if (!container || !row) return;
    directMessageHandled.current = requestedMessage;
    stickToBottom.current = false;
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const centeredTop = container.scrollTop
      + rowRect.top
      - containerRect.top
      - ((container.clientHeight - rowRect.height) / 2);
    container.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
    row.classList.remove('direct-message'); void row.offsetWidth; row.classList.add('direct-message');
    const timer = window.setTimeout(() => row.classList.remove('direct-message'), 2600);
    return () => window.clearTimeout(timer);
  }, [messages, loading, requestedMessage]);

  useEffect(() => {
    if (!pendingMilestone) return;
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [pendingMilestone]);

  const submit = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    setActiveMention(null);
    stickToBottom.current = true;
    setAtBottom(true);
    try {
      const message = await send(text, { milestoneId: pendingMilestone?._id });
      onMessageSent?.(message);
      onPendingMilestoneChange?.(null);
    } catch {
      setDraft(text);
    }
  };

  const updateDraft = (event) => {
    const value = event.target.value;
    setDraft(value);
    setActiveMention(mentionAtCaret(value, event.target.selectionStart));
    setMentionIndex(0);
  };

  const selectMention = (person) => {
    if (!activeMention) return;
    const insertion = `@${person.handle} `;
    const next = draft.slice(0, activeMention.start) + insertion + draft.slice(activeMention.end);
    const nextCaret = activeMention.start + insertion.length;
    setDraft(next);
    setActiveMention(null);
    setMentionIndex(0);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const onKeyDown = (event) => {
    if (activeMention && mentionMatches.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionIndex((index) => (index + 1) % mentionMatches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionIndex((index) => (index - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectMention(mentionMatches[mentionIndex] || mentionMatches[0]);
        return;
      }
    }
    if (activeMention && event.key === 'Escape') {
      event.preventDefault();
      setActiveMention(null);
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(event);
    }
  };

  const startEdit = (message) => {
    setEditingId(message._id);
    setEditDraft(message.body);
    stickToBottom.current = false;
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = async (message) => {
    const text = editDraft.trim();
    if (!text || savingEdit) return;
    if (text === message.body) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      await edit(message._id, text);
      cancelEdit();
    } catch {
      // The hook keeps the request error visible and the draft available.
    } finally {
      setSavingEdit(false);
    }
  };

  const onEditKeyDown = (event, message) => {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); saveEdit(message); }
    if (event.key === 'Escape') { event.preventDefault(); cancelEdit(); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deletingId) return;
    setDeletingId(deleteTarget._id);
    try {
      await remove(deleteTarget._id);
      if (editingId === deleteTarget._id) cancelEdit();
      onMessageDeleted?.(deleteTarget);
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="thread-root" style={{ '--thread-height': height }}>
      <div className="thread-scroll-shell">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="scroll-hidden thread-scroll"
          role="log"
          aria-live="polite"
          aria-label="Conversation messages"
        >
          {loading ? (
            <div className="thread-loading" role="status"><span className="spinner" /><span>Loading conversation…</span></div>
          ) : messages.length === 0 ? (
            <div className="thread-empty">
              <span><MessageSquareText size={22} /></span>
              <strong>No messages yet</strong>
              <p>Start the conversation below.</p>
            </div>
          ) : messages.map((message, index) => {
            const previous = messages[index - 1];
            const mine = authorIdOf(message) === String(user?.id || user?._id);
            const isFeedback = message.kind === 'milestone_feedback';
            const isEditing = editingId === message._id;
            const startsDay = !previous || !isSameDay(previous.createdAt, message.createdAt);
            const grouped = Boolean(previous)
              && !startsDay
              && !isFeedback
              && previous.kind !== 'milestone_feedback'
              && authorIdOf(previous) === authorIdOf(message)
              && new Date(message.createdAt) - new Date(previous.createdAt) < 5 * 60 * 1000;
            const roleLabel = message.authorRole === 'client' ? 'Client' : 'Evercrest team';

            return (
              <React.Fragment key={message._id}>
                {startsDay && <div className="thread-date-separator" role="separator"><span>{fmtDay(message.createdAt)}</span></div>}
                <article id={`thread-message-${message._id}`} className={`thread-message-row ${mine ? 'mine' : ''} ${grouped ? 'grouped' : ''}`}>
                  {grouped ? <span className="thread-avatar placeholder" aria-hidden="true" /> : <UserIdentity person={message.authorProfile} userId={authorIdOf(message)} name={message.authorName} role={message.authorRole} size="sm" compact className="thread-avatar-identity" />}
                  <div className="thread-message-stack">
                    {!grouped && (
                      <header className="thread-message-meta">
                        <UserIdentity person={message.authorProfile} userId={authorIdOf(message)} name={message.authorName} role={message.authorRole} hideAvatar className="thread-author-identity" />
                        <span className="thread-author-role">{roleLabel}</span>
                        <time dateTime={message.createdAt}>{fmtTime(message.createdAt)}</time>
                        {message.editedAt && <span title={`Edited ${fmtTime(message.editedAt)}`}>(edited)</span>}
                      </header>
                    )}

                    {isFeedback && (
                      <div className="thread-feedback-label">
                        <AlertTriangle size={13} />
                        <span>Client feedback{message.milestoneTitle ? ` · ${message.milestoneTitle}` : ''}</span>
                      </div>
                    )}

                    {message.milestoneId && message.milestoneTitle && (
                      <button
                        type="button"
                        className="thread-milestone-reference"
                        onClick={() => onMilestoneClick?.(String(message.milestoneId))}
                        title={`Go to milestone: ${message.milestoneTitle}`}
                      >
                        <Flag size={13} /><span>{message.milestoneTitle}</span>
                      </button>
                    )}

                    {isEditing ? (
                      <div className="thread-edit-card">
                        <textarea
                          className="input"
                          value={editDraft}
                          autoFocus
                          onChange={(event) => setEditDraft(event.target.value)}
                          onKeyDown={(event) => onEditKeyDown(event, message)}
                          rows={3}
                          aria-label="Edit message"
                        />
                        <div className="thread-edit-footer">
                          <span><kbd>Enter</kbd> save · <kbd>Esc</kbd> cancel</span>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={savingEdit}><X size={14} />Cancel</button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => saveEdit(message)} disabled={savingEdit || !editDraft.trim()}>{savingEdit ? <span className="spinner" /> : <><Check size={14} />Save</>}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="thread-message-line">
                        <div className={`thread-message-bubble ${mine ? 'mine' : ''} ${isFeedback ? 'feedback' : ''}`}>{renderMessageBody(message.body)}</div>
                        {mine && (
                          <div className="thread-message-actions" aria-label="Message actions">
                            <button type="button" onClick={() => startEdit(message)} title="Edit message" aria-label="Edit message"><Pencil size={14} /></button>
                            <button type="button" className="danger" onClick={() => setDeleteTarget(message)} title="Delete message" aria-label="Delete message"><Trash2 size={14} /></button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              </React.Fragment>
            );
          })}
        </div>

        {!atBottom && messages.length > 0 && (
          <button type="button" className="thread-jump-latest" onClick={jumpToLatest}><ArrowDown size={14} />Jump to latest</button>
        )}
      </div>

      {error && <div className="thread-error" role="alert"><AlertTriangle size={14} /><span>{error}</span></div>}

      <form className={`thread-composer ${draft.includes('\n') ? 'multiline' : ''}`} onSubmit={submit}>
        {pendingMilestone && (
          <div className="thread-pending-milestone">
            <Flag size={13} />
            <span>Referencing</span>
            <strong>{pendingMilestone.title}</strong>
            <button type="button" onClick={() => onPendingMilestoneChange?.(null)} aria-label={`Remove ${pendingMilestone.title} reference`} title="Remove milestone reference"><X size={13} /></button>
          </div>
        )}

        <div className="thread-composer-shell">
          <div className="thread-composer-input">
            {activeMention && (
              <div className="mention-suggestions" role="listbox" aria-label="People you can mention">
                {mentionMatches.length ? mentionMatches.map((person, index) => (
                  <button
                    key={person.id}
                    type="button"
                    role="option"
                    aria-selected={index === mentionIndex}
                    className={`mention-suggestion${index === mentionIndex ? ' active' : ''}`}
                    onMouseDown={(event) => { event.preventDefault(); selectMention(person); }}
                    onMouseEnter={() => setMentionIndex(index)}
                  >
                    <PersonAvatar person={person} size="sm" />
                    <span className="mention-suggestion-copy"><strong>{person.name}</strong><small>@{person.handle}</small></span>
                    <span className="mention-suggestion-role">{person.role === 'client' ? 'Client' : 'Team'}</span>
                  </button>
                )) : <div className="mention-suggestions-empty">No matching people</div>}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={updateDraft}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              rows={1}
              aria-label="Message"
              aria-autocomplete="list"
              disabled={!enabled}
            />
          </div>
          <div className="thread-composer-footer">
            <span className="thread-composer-hint"><kbd>Enter</kbd> to send <i>·</i> <kbd>Shift + Enter</kbd> for a new line</span>
            <span className="thread-composer-mention-hint">Use <kbd>@</kbd> to mention someone</span>
            <button type="submit" className="btn btn-primary thread-send-button" aria-label="Send message" disabled={sending || !draft.trim()}>
                {sending ? <span className="spinner" /> : <><Send size={16} /><span className="thread-send-label">Send</span></>}
            </button>
          </div>
        </div>
      </form>

      {deleteTarget && (
        <ConfirmDialog
          title="Delete message?"
          message="This message will be removed from the conversation for everyone. This cannot be undone."
          confirmLabel="Delete message"
          isDangerous
          isLoading={deletingId === deleteTarget._id}
          loadingLabel="Deleting…"
          onCancel={() => { if (!deletingId) setDeleteTarget(null); }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
